import { useState, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { workOrdersApi } from '../../services/api'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle, Search, XCircle } from 'lucide-react'
import type { VerificaFrazionarioResult, CorrezioneWO } from '../../types/index'

interface Props {
  result: VerificaFrazionarioResult
  onClose: () => void
  onSuccess: () => void
  fonte?: 'pdf' | 'poste'
}

interface RigaCorrezione {
  key: string
  wo_id: number
  numero_progressivo: string
  frazionario: string
  nome_ufficio_pdf: string
  campo: 'comune' | 'indirizzo' | 'cap'
  attuale: string
  suggerito: string
}

export default function VerificaFrazionarioModal({ result, onClose, onSuccess, fonte = 'pdf' }: Props) {
  const queryClient = useQueryClient()

  // Espande discrepanze in righe singole per campo
  const righe: RigaCorrezione[] = useMemo(() => {
    const out: RigaCorrezione[] = []
    for (const d of result.discrepanze) {
      for (const c of d.campi_diversi) {
        out.push({
          key: `${d.wo_id}-${c.campo}`,
          wo_id: d.wo_id,
          numero_progressivo: d.numero_progressivo,
          frazionario: d.frazionario,
          nome_ufficio_pdf: d.nome_ufficio_pdf,
          campo: c.campo as 'comune' | 'indirizzo' | 'cap',
          attuale: c.attuale,
          suggerito: c.suggerito,
        })
      }
    }
    return out
  }, [result])

  const [selected, setSelected] = useState<Set<string>>(new Set(
    righe.filter(r => r.campo === 'comune').map(r => r.key)
  ))

  const toggleAll = () => {
    if (selected.size === righe.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(righe.map(r => r.key)))
    }
  }

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const mutation = useMutation({
    mutationFn: () => {
      const byWo = new Map<number, CorrezioneWO>()
      for (const riga of righe) {
        if (!selected.has(riga.key)) continue
        const existing = byWo.get(riga.wo_id) ?? { wo_id: riga.wo_id }
        if (riga.campo === 'comune') existing.comune = riga.suggerito
        if (riga.campo === 'indirizzo') existing.indirizzo = riga.suggerito
        if (riga.campo === 'cap') existing.cap = riga.suggerito
        byWo.set(riga.wo_id, existing)
      }
      return workOrdersApi.applicaCorrezioni(Array.from(byWo.values()))
    },
    onSuccess: (res) => {
      const count = (res.data as { aggiornati: number }).aggiornati
      toast.success(`${count} pratiche aggiornate`)
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      onSuccess()
    },
    onError: () => toast.error('Errore durante l\'applicazione delle correzioni'),
  })

  const totaleAnalizzate = result.discrepanze.length + result.non_trovati.length +
    result.senza_frazionario.length + result.totale_ok

  const [showNonTrovati, setShowNonTrovati] = useState(false)
  const [showSenzaFraz, setShowSenzaFraz] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget && !mutation.isPending) onClose() }}>
      <div className="card w-full max-w-3xl animate-fade-up flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="p-5 border-b flex items-start justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Search size={16} className="text-blue-600" /> Verifica Frazionario
              {fonte === 'poste' && <span className="text-xs font-normal text-slate-400">(fonte: blia.it, dati Poste live)</span>}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {totaleAnalizzate} pratiche analizzate ·{' '}
              <span className="text-amber-600 font-medium">{result.discrepanze.length} discrepanze</span> ·{' '}
              <span className="text-slate-400">{result.non_trovati.length} non trovati</span> ·{' '}
              <span className="text-emerald-600 font-medium">{result.totale_ok} ok</span>
            </p>
          </div>
          <button onClick={onClose} disabled={mutation.isPending} className="btn-ghost px-2">✕</button>
        </div>

        {/* Body scrollabile */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">

          {/* Tabella discrepanze */}
          {righe.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Discrepanze rilevate ({righe.length} campi)
                </p>
                <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
                  {selected.size === righe.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                </button>
              </div>

              {/* Avviso indirizzo */}
              {righe.some(r => r.campo === 'indirizzo') && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 mb-3">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  L'indirizzo suggerito è quello dell'ufficio postale, non del cliente. Valuta con attenzione prima di applicare.
                </div>
              )}

              <div className="border rounded-lg overflow-hidden text-xs">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox"
                          checked={selected.size === righe.length && righe.length > 0}
                          onChange={toggleAll}
                          className="cursor-pointer" />
                      </th>
                      <th className="text-left px-3 py-2 text-slate-600 font-semibold">Pratica</th>
                      <th className="text-left px-3 py-2 text-slate-600 font-semibold">Frazionario</th>
                      <th className="text-left px-3 py-2 text-slate-600 font-semibold">Campo</th>
                      <th className="text-left px-3 py-2 text-slate-600 font-semibold">Attuale</th>
                      <th className="text-left px-3 py-2 text-slate-600 font-semibold">Suggerito</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {righe.map(r => (
                      <tr key={r.key}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggle(r.key)}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selected.has(r.key)}
                            onChange={() => toggle(r.key)}
                            onClick={e => e.stopPropagation()}
                            className="cursor-pointer" />
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700">{r.numero_progressivo}</td>
                        <td className="px-3 py-2 font-mono text-slate-500">{r.frazionario}</td>
                        <td className="px-3 py-2">
                          <span className={`badge ${r.campo === 'comune' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                            {r.campo}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500 line-through">{r.attuale || '—'}</td>
                        <td className="px-3 py-2 text-emerald-700 font-medium">{r.suggerito}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-emerald-50 rounded-lg text-emerald-700 text-sm">
              <CheckCircle size={16} />
              Nessuna discrepanza rilevata per le pratiche analizzate.
            </div>
          )}

          {/* Non trovati */}
          {result.non_trovati.length > 0 && (
            <div>
              <button
                onClick={() => setShowNonTrovati(v => !v)}
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 font-semibold uppercase tracking-wider">
                <XCircle size={13} className="text-slate-400" />
                Frazionario non nel dataset ({result.non_trovati.length})
                <span>{showNonTrovati ? '▲' : '▼'}</span>
              </button>
              {showNonTrovati && (
                <div className="mt-2 border rounded-lg overflow-hidden text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-3 py-2 text-slate-600 font-semibold">Pratica</th>
                        <th className="text-left px-3 py-2 text-slate-600 font-semibold">Frazionario</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.non_trovati.map(r => (
                        <tr key={r.wo_id}>
                          <td className="px-3 py-2 font-mono text-slate-700">{r.numero_progressivo}</td>
                          <td className="px-3 py-2 font-mono text-slate-500">{r.frazionario}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Senza frazionario */}
          {result.senza_frazionario.length > 0 && (
            <div>
              <button
                onClick={() => setShowSenzaFraz(v => !v)}
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 font-semibold uppercase tracking-wider">
                <AlertTriangle size={13} className="text-slate-400" />
                Senza frazionario ({result.senza_frazionario.length})
                <span>{showSenzaFraz ? '▲' : '▼'}</span>
              </button>
              {showSenzaFraz && (
                <div className="mt-2 border rounded-lg overflow-hidden text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-3 py-2 text-slate-600 font-semibold">Pratica</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.senza_frazionario.map(r => (
                        <tr key={r.wo_id}>
                          <td className="px-3 py-2 font-mono text-slate-700">{r.numero_progressivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t flex gap-3 shrink-0">
          <button onClick={onClose} disabled={mutation.isPending} className="btn-secondary flex-1">
            Annulla
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={selected.size === 0 || mutation.isPending}
            className="btn-primary flex-1">
            {mutation.isPending
              ? 'Applico...'
              : `Applica ${selected.size} correzione${selected.size !== 1 ? 'i' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
